import { supabase } from '@/lib/supabase';
import { pushNotification } from '@/lib/notifications';

// ─────────────────────────────────────────────────────────────
// besoins — place de marché des besoins entre comptes Zifek.
//
// Un compte (commerce ou client) publie un besoin (« je cherche un
// livreur »). Le besoin est annoncé aux professionnels de la catégorie
// concernée : ils reçoivent une notification, consultent le besoin,
// puis soit acceptent le prix proposé par le demandeur, soit font leur
// propre proposition de prix. Le demandeur choisit la proposition qui
// l'arrange : le besoin est attribué et disparaît des autres comptes.
// ─────────────────────────────────────────────────────────────

export interface Besoin {
  id: number;
  user_id: number;
  auteur_nom: string | null;
  titre: string;
  description: string | null;
  categorie: string | null;
  categorie_id: number | null;
  ville: string | null;
  budget: number | null;
  monaie: string | null;
  statut: string;
  professionnel_id: number | null;
  annonce_le: string | null;
  created_at: string;
  updated_at: string;
}

export interface BesoinProposition {
  id: number;
  besoin_id: number;
  user_id: number;
  auteur_nom: string | null;
  /** 'acceptation' = le pro accepte le prix du demandeur · 'offre' = son propre prix. */
  type: string;
  prix: number | null;
  monaie: string | null;
  message: string | null;
  statut: string;
  created_at: string;
}

export interface BesoinCategorie {
  id: number;
  nom: string;
}

export interface BesoinInput {
  titre: string;
  description: string;
  categorie: string;
  categorieId: number | null;
  ville: string;
  budget: number | null;
  monaie: string;
}

const SELECT_BESOIN =
  'id, user_id, auteur_nom, titre, description, categorie, categorie_id, ville, budget, monaie, statut, professionnel_id, annonce_le, created_at, updated_at';

/** Catégories = types de compte professionnels actifs (hors Client). */
export async function fetchBesoinCategories(): Promise<BesoinCategorie[]> {
  try {
    const { data, error } = await supabase
      .from('typecompte')
      .select('id, nom, statustypecompte, actif')
      .order('nom', { ascending: true });
    if (error || !data) return [];
    return (data as { id: number; nom: string; statustypecompte?: number; actif?: boolean }[])
      .filter((row) => Number(row.statustypecompte) !== 1 && row.actif !== false)
      .map((row) => ({ id: Number(row.id), nom: String(row.nom || '') }));
  } catch {
    return [];
  }
}

export async function fetchBesoins(): Promise<Besoin[]> {
  const { data, error } = await supabase
    .from('besoins')
    .select(SELECT_BESOIN)
    .order('created_at', { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data || []) as Besoin[];
}

export async function fetchBesoinPropositions(besoinIds: number[]): Promise<BesoinProposition[]> {
  if (besoinIds.length === 0) return [];
  const { data, error } = await supabase
    .from('besoin_propositions')
    .select('id, besoin_id, user_id, auteur_nom, type, prix, monaie, message, statut, created_at')
    .in('besoin_id', besoinIds)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []) as BesoinProposition[];
}

export async function createBesoin(userId: number, auteurNom: string, input: BesoinInput): Promise<Besoin> {
  const { data, error } = await supabase
    .from('besoins')
    .insert({
      user_id: userId,
      auteur_nom: auteurNom,
      titre: input.titre.trim(),
      description: input.description.trim(),
      categorie: input.categorie || null,
      categorie_id: input.categorieId,
      ville: input.ville.trim() || null,
      budget: input.budget,
      monaie: input.monaie,
      statut: 'ouvert',
    })
    .select(SELECT_BESOIN)
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error('Le besoin n’a pas pu être créé.');
  return data as Besoin;
}

/** Met à jour un besoin (statut, professionnel retenu, date d'annonce). */
export async function updateBesoin(
  id: number,
  patch: Partial<Pick<Besoin, 'statut' | 'professionnel_id' | 'annonce_le'>>,
): Promise<void> {
  const { error } = await supabase
    .from('besoins')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

interface AnnonceResult {
  notified: number;
  message: string;
}

/**
 * Annonce le besoin aux professionnels concernés (même catégorie d'activité)
 * via la cloche de notifications + notification push (OneSignal / Web Push).
 * Le clic sur la notification ouvre directement la page Besoins.
 */
export async function annoncerBesoin(besoin: Besoin): Promise<AnnonceResult> {
  let notified = 0;
  const zone = (besoin.ville || '').trim();
  try {
    let query = supabase
      .from('users')
      .select('id, typecompte, Ville')
      .neq('id', besoin.user_id)
      .limit(500);
    if (besoin.categorie_id) query = query.eq('typecompte', besoin.categorie_id);
    const { data } = await query;
    // 🎯 Filtre zone : on ne notifie que les pros de la même ville / région.
    // Comparaison souple (insensible à la casse, sans accents parasites) afin
    // que « Brazzaville » et « brazzaville » soient bien reconnus.
    const normalize = (v: string) =>
      v.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
    const zoneKey = normalize(zone);
    const targets = ((data || []) as { id: number; typecompte: number | null; Ville?: string | null }[])
      .filter((t) => {
        if (!zoneKey) return true;
        const ville = normalize(String(t.Ville || ''));
        return ville.includes(zoneKey) || zoneKey.includes(ville);
      });
    const montant = Number(besoin.budget) || 0;
    const details = [
      besoin.categorie ? `Catégorie : ${besoin.categorie}.` : '',
      besoin.ville ? `Zone : ${besoin.ville}.` : '',
      montant > 0 ? `Budget proposé : ${montant.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${besoin.monaie || 'MAD'}.` : '',
    ].filter(Boolean).join(' ');
    for (const target of targets) {
      if (!Number(target.id)) continue;
      await pushNotification({
        userId: Number(target.id),
        type: 'besoin',
        publicationId: besoin.id,
        message: `Nouveau besoin : ${besoin.titre}. ${details}`.slice(0, 240),
      });
      notified += 1;
    }
    await updateBesoin(besoin.id, { annonce_le: new Date().toISOString() });
  } catch {
    return { notified, message: 'L’annonce a été partiellement envoyée.' };
  }
  if (notified === 0) {
    const details = [besoin.categorie ? `de la catégorie « ${besoin.categorie} »` : '', zone ? `dans la zone « ${zone} »` : '']
      .filter(Boolean)
      .join(' ');
    return {
      notified,
      message: `Aucun professionnel ${details || 'concerné'} pour le moment. Retirez la ville ou changez de catégorie pour élargir l’annonce.`,
    };
  }
  return {
    notified,
    message: `${notified} professionnel${notified > 1 ? 's' : ''} notifié${notified > 1 ? 's' : ''}${zone ? ` dans la zone « ${zone} »` : ''} (notification push incluse).`,
  };
}

/**
 * Le demandeur révise son prix après avoir vu les offres reçues.
 * Les professionnels qui ont déjà postulé (et le pro retenu, le cas échéant)
 * sont notifiés de la nouvelle proposition.
 */
export async function modifierPrixBesoin(
  besoin: Besoin,
  budget: number | null,
  monaie: string,
): Promise<void> {
  const { error } = await supabase
    .from('besoins')
    .update({ budget, monaie, updated_at: new Date().toISOString() })
    .eq('id', besoin.id);
  if (error) throw error;

  const montant = Number(budget) || 0;
  const texte = montant > 0
    ? `Nouveau prix proposé : ${montant.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${monaie}.`
    : 'Le demandeur souhaite désormais négocier le prix (plus de montant fixe).';

  try {
    const cibles = new Set<number>();
    if (besoin.professionnel_id) cibles.add(Number(besoin.professionnel_id));
    const { data } = await supabase
      .from('besoin_propositions')
      .select('user_id')
      .eq('besoin_id', besoin.id);
    ((data || []) as { user_id: number }[]).forEach((row) => {
      const id = Number(row.user_id);
      if (id > 0) cibles.add(id);
    });
    for (const userId of cibles) {
      await pushNotification({
        userId,
        type: 'besoin',
        publicationId: besoin.id,
        message: `« ${besoin.titre} » — ${texte} Acceptez ou ajustez votre offre.`,
      });
    }
  } catch {
    /* best-effort */
  }
}

/** Le professionnel accepte le prix proposé par le demandeur → il gagne le besoin. */
export async function accepterPrixDuDemandeur(
  besoin: Besoin,
  userId: number,
  auteurNom: string,
): Promise<void> {
  const { error } = await supabase.from('besoin_propositions').upsert(
    {
      besoin_id: besoin.id,
      user_id: userId,
      auteur_nom: auteurNom,
      type: 'acceptation',
      prix: besoin.budget,
      monaie: besoin.monaie || 'MAD',
      message: 'Prix du demandeur accepté.',
      statut: 'acceptee',
    },
    { onConflict: 'besoin_id,user_id' },
  );
  if (error) throw error;
  await updateBesoin(besoin.id, { statut: 'attribue', professionnel_id: userId });
  await pushNotification({
    userId: besoin.user_id,
    type: 'besoin',
    publicationId: besoin.id,
    message: `${auteurNom} a accepté votre prix pour « ${besoin.titre} ». Contactez-le pour les détails.`,
  });
}

/** Le professionnel fait sa propre proposition de prix. */
export async function proposerUnPrix(
  besoin: Besoin,
  userId: number,
  auteurNom: string,
  prix: number,
  message: string,
): Promise<void> {
  const { error } = await supabase.from('besoin_propositions').upsert(
    {
      besoin_id: besoin.id,
      user_id: userId,
      auteur_nom: auteurNom,
      type: 'offre',
      prix,
      monaie: besoin.monaie || 'MAD',
      message: message.trim() || null,
      statut: 'proposee',
    },
    { onConflict: 'besoin_id,user_id' },
  );
  if (error) throw error;
  await pushNotification({
    userId: besoin.user_id,
    type: 'besoin',
    publicationId: besoin.id,
    message: `${auteurNom} a proposé ${prix.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${besoin.monaie || 'MAD'} pour « ${besoin.titre} ».`,
  });
}

/** Le demandeur retient une proposition : le besoin est attribué. */
export async function accepterProposition(
  besoin: Besoin,
  proposition: BesoinProposition,
): Promise<void> {
  const { error } = await supabase
    .from('besoin_propositions')
    .update({ statut: 'acceptee' })
    .eq('id', proposition.id);
  if (error) throw error;
  await supabase
    .from('besoin_propositions')
    .update({ statut: 'refusee' })
    .eq('besoin_id', besoin.id)
    .neq('id', proposition.id);
  await updateBesoin(besoin.id, { statut: 'attribue', professionnel_id: proposition.user_id });
  await pushNotification({
    userId: proposition.user_id,
    type: 'besoin',
    publicationId: besoin.id,
    message: `Votre proposition pour « ${besoin.titre} » a été retenue. Contactez le demandeur pour finaliser.`,
  });
}

/** Clôture un besoin (demandeur). */
export async function cloturerBesoin(id: number): Promise<void> {
  await updateBesoin(id, { statut: 'clos' });
}

/** Notification aux comptes concernés qu'une proposition a été reçue (superadmin inclus). */
export async function notifierSuperAdminsBesoin(besoin: Besoin, texte: string): Promise<void> {
  try {
    const { data } = await supabase.from('users').select('id').in('typecompte', [0, 1]).limit(5);
    for (const admin of (data || []) as { id: number }[]) {
      await pushNotification({
        userId: Number(admin.id),
        type: 'besoin',
        publicationId: besoin.id,
        message: texte.slice(0, 240),
      });
    }
  } catch {
    /* best-effort */
  }
}